"use client"

import { useQuery } from "@tanstack/react-query"

import {
  fetchIdentityPreview,
  identityRefIsResolvable,
  type IdentityPreviewDto,
  type IdentityRef,
} from "@/shared/lib/api/identity-api"

export const identityPreviewQueryKeys = {
  all: ["identity-preview"] as const,
  detail: (ref: IdentityRef) =>
    [
      ...identityPreviewQueryKeys.all,
      {
        memberId: ref.memberId ?? "__member__",
        userId: ref.userId ?? "__user__",
        email: ref.email ?? "__email__",
      },
    ] as const,
}

/**
 * Resolve a person's merged identity (directory member <-> workspace user) lazily. Pass `enabled`
 * false until the surface (popover/hover/dialog) actually opens so tables never fan out a request
 * per row — the card shows the caller's fallback until the fetch lands and enriches it.
 */
export function useIdentityPreview(ref: IdentityRef, options?: { enabled?: boolean }) {
  const enabled = (options?.enabled ?? true) && identityRefIsResolvable(ref)

  return useQuery<IdentityPreviewDto | null>({
    queryKey: identityPreviewQueryKeys.detail(ref),
    queryFn: () => fetchIdentityPreview(ref),
    enabled,
    staleTime: 5 * 60_000,
  })
}
